import React, { useState, useEffect, useMemo } from 'react';
import { ExamService, CourseService } from '../services/api';
import Modal from './common/Modal';
import { getTerminology } from '../utils/terminology';
import { useSettings } from '../context/SettingsContext';
import Swal from 'sweetalert2';

import { exportToExcel } from '../utils/exportHelper';

const ExamList = () => {
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user] = useState(JSON.parse(localStorage.getItem('user')));
  const userRole = user?.role || user?.data?.role;
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const canExport = isAdmin;
  const staffPerms = user?.staff?.permissions || user?.data?.staff?.permissions || [];
  const canManageExams = isAdmin || staffPerms.includes('manage_exams');
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterBatches, setFilterBatches] = useState([]);

  const { settings } = useSettings();
  const terminology = React.useMemo(() => {
    return getTerminology(settings.school_type);
  }, [settings.school_type]);

  const term = terminology.course;
  const terms = terminology.courses;
  const batchTerm = terminology.batch;
  const batchesTerm = terminology.batches;
  const studentTerm = terminology.student;

  const [formData, setFormData] = useState({
    name: '',
    course_id: '',
    batch_id: '',
    exam_date: '',
    total_marks: ''
  });

  const [stats, setStats] = useState({ total_exams: 0, pending: 0, completed: 0 });
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examStudents, setExamStudents] = useState([]);
  const [marksData, setMarksData] = useState({});
  const [savingResults, setSavingResults] = useState(false);
  const [showSheetModal, setShowSheetModal] = useState(false);
  const [sheetData, setSheetData] = useState({ exam: null, results: [] });

  useEffect(() => {
    loadExams();
    loadCourses();
  }, []);

  const loadExams = async () => {
    try {
      const response = await ExamService.getAll();
      const data = response.data;
      setExams(data);
      calculateStats(data);
    } catch (error) {
      console.error("Error loading exams", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const total_exams = data.length;
    // Mock logic for status
    const pending = data.filter(e => new Date(e.exam_date) > new Date()).length;
    const completed = total_exams - pending;
    setStats({ total_exams, pending, completed });
  };

  const loadCourses = async () => {
    try {
      const response = await CourseService.getAll();
      setCourses(response.data);
    } catch (error) {
      console.error("Error loading courses", error);
    }
  };

  const handleFilterCourseChange = (e) => {
    const courseId = e.target.value;
    setFilterCourse(courseId);
    setFilterBatch('');
    if (courseId) {
      const selected = courses.find(c => c.id === parseInt(courseId));
      setFilterBatches(selected?.batches || []);
    } else {
      setFilterBatches([]);
    }
  };

  const handleCourseChange = (e) => {
    const courseId = e.target.value;
    setFormData({ ...formData, course_id: courseId, batch_id: '' });

    if (courseId) {
      const selectedCourse = courses.find(c => c.id === parseInt(courseId));
      if (selectedCourse && selectedCourse.batches) {
        setBatches(selectedCourse.batches);
      } else {
        setBatches([]);
      }
    } else {
      setBatches([]);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await ExamService.create(formData);
      setShowModal(false);
      loadExams();
      setFormData({ name: '', course_id: '', batch_id: '', exam_date: '', total_marks: '' });
      Swal.fire({ icon: 'success', title: 'Success', text: 'Exam scheduled successfully!', timer: 1500 });
    } catch (error) {
      console.error("Error creating exam", error);
    }
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete exam "${name}"? This will also remove all candidate results.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await ExamService.delete(id);
        loadExams();
        Swal.fire('Deleted!', 'Exam has been deleted.', 'success');
      } catch (error) {
        console.error("Error deleting exam", error);
      }
    }
  };

  const handleManageResults = async (exam) => {
    try {
      setSelectedExam(exam);
      const res = await ExamService.getStudents(exam.id);
      setExamStudents(res.data.students);

      const initialMarks = {};
      res.data.students.forEach(s => {
        initialMarks[s.id] = res.data.results[s.id]?.obtained_marks || '';
      });
      setMarksData(initialMarks);
      setShowResultsModal(true);
    } catch (error) {
      console.error("Error loading exam students", error);
    }
  };

  const handleSaveResults = async () => {
    try {
      setSavingResults(true);
      const resultsArray = Object.entries(marksData).map(([studentId, marks]) => ({
        student_id: studentId,
        obtained_marks: marks || 0
      }));

      await ExamService.saveResults(selectedExam.id, { results: resultsArray });
      setShowResultsModal(false);
      loadExams(); // Refresh to update stats
      Swal.fire({ icon: 'success', title: 'Saved!', text: 'Marks updated successfully!', timer: 1500 });
    } catch (error) {
      console.error("Error saving results", error);
    } finally {
      setSavingResults(false);
    }
  };

  const handleGenerateSheet = async (exam) => {
    try {
      setLoading(true);
      const res = await ExamService.getStudents(exam.id);
      const students = res.data.students;
      const resultsMap = res.data.results;

      const formattedResults = students.map(s => {
        const result = resultsMap[s.id];
        const obtained = result ? result.obtained_marks : 0;
        const total = exam.total_marks;
        const percentage = total > 0 ? ((obtained / total) * 100).toFixed(1) : 0;
        const passingGrade = settings.passing_percentage || 40;

        return {
          ...s,
          obtained,
          percentage,
          status: parseFloat(percentage) >= parseFloat(passingGrade) ? 'Pass' : 'Fail'
        };
      });

      setSheetData({ exam, results: formattedResults });
      setShowSheetModal(true);
    } catch (error) {
      console.error("Error generating sheet", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredExams = useMemo(() => {
    return exams.filter(exam => {
      // Search Filter
      const matchesSearch = exam.name.toLowerCase().includes(searchTerm.toLowerCase());

      // Course Filter
      const matchesCourse = !filterCourse || exam.course_id === parseInt(filterCourse);

      // Batch Filter
      const matchesBatch = !filterBatch || exam.batch_id === parseInt(filterBatch);

      return matchesSearch && matchesCourse && matchesBatch;
    });
  }, [exams, searchTerm, filterCourse, filterBatch]);

  const handleExport = () => {
    if (filteredExams.length === 0) {
      Swal.fire('No Data', 'No exam records to export.', 'info');
      return;
    }

    const exportData = filteredExams.map(exam => ({
      'Exam Name': exam.name,
      [term]: exam.course?.name,
      [batchTerm]: exam.batch?.name || `No ${batchTerm}`,
      'Date': new Date(exam.exam_date).toLocaleDateString(),
      'Total Marks': exam.total_marks
    }));

    exportToExcel(exportData, `Exams_List_Export_${new Date().toISOString().split('T')[0]}`);
  };


  return (
    <div className="container-fluid p-0">
      <div className="section-header mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h2 className="fw-bold">Exams & Results</h2>
          <p className="text-muted">Manage exam schedules and {studentTerm.toLowerCase()} performance</p>
        </div>
        <div className="d-flex gap-2">
          {canExport && (
            <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={handleExport}>
              <i className="bi bi-file-earmark-spreadsheet"></i> Export
            </button>
          )}
          {canManageExams && (
            <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={() => setShowModal(true)}>
              <i className="bi bi-plus-lg"></i> Schedule Exam
            </button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid mb-5">
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Total Exams</p>
            <h3 className="mb-0">{stats.total_exams}</h3>
          </div>
          <div className="stat-icon bg-blue-light">
            <i className="bi bi-file-earmark-text-fill"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Upcoming</p>
            <h3 className="mb-0 text-primary">{stats.pending}</h3>
          </div>
          <div className="stat-icon bg-orange-light">
            <i className="bi bi-clock-history"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Completed</p>
            <h3 className="mb-0 text-success">{stats.completed}</h3>
          </div>
          <div className="stat-icon bg-green-light">
            <i className="bi bi-check-all"></i>
          </div>
        </div>
      </div>

      <div className="card-box shadow-sm border">
        <div className="section-header px-4 pt-4 d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0 fw-bold">Exam Records</h5>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="position-relative">
              <i className="bi bi-search position-absolute text-muted" style={{ left: '15px', top: '50%', transform: 'translateY(-50%)' }}></i>
              <input
                type="text"
                className="form-control rounded-pill ps-5 bg-light border-0"
                placeholder="Search exams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '220px', height: '40px' }}
              />
            </div>

            <select
              className="form-select form-select-sm rounded-pill border-0 shadow-sm bg-light px-3"
              style={{ width: '160px', height: '40px' }}
              value={filterCourse}
              onChange={handleFilterCourseChange}
            >
              <option value="">All {terms}</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>

            <select
              className="form-select form-select-sm rounded-pill border-0 shadow-sm bg-light px-3"
              style={{ width: '160px', height: '40px' }}
              value={filterBatch}
              onChange={(e) => setFilterBatch(e.target.value)}
              disabled={!filterCourse}
            >
              <option value="">All {batchesTerm}</option>
              {filterBatches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="ps-4">Exam Name</th>
                <th>{term} / {batchTerm}</th>
                <th>Date</th>
                <th>Marks</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-5 text-muted">Loading exams...</td></tr>
              ) : filteredExams.length > 0 ? (
                filteredExams.map((exam) => (
                  <tr key={exam.id}>
                    <td className="ps-4 fw-bold text-dark">{exam.name}</td>
                    <td>
                      <div className="d-flex flex-column">
                        <span className="fw-semibold text-primary">{exam.course?.name}</span>
                        <span className="small text-muted">{exam.batch?.name || `No ${batchTerm}`}</span>
                      </div>
                    </td>
                    <td><span className="text-muted"><i className="bi bi-calendar3 me-2"></i>{new Date(exam.exam_date).toLocaleDateString()}</span></td>
                    <td><span className="badge bg-light text-dark border">{exam.total_marks} Marks</span></td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          className="btn btn-icon btn-light rounded-circle text-primary shadow-sm d-flex align-items-center justify-content-center"
                          style={{ width: 35, height: 35 }}
                          title="Add/Mark Results"
                          onClick={() => handleManageResults(exam)}
                        >
                          <i className="bi bi-person-check-fill"></i>
                        </button>
                        <button
                          className="btn btn-icon btn-light rounded-circle text-success shadow-sm d-flex align-items-center justify-content-center"
                          style={{ width: 35, height: 35 }}
                          title="View Result Sheet"
                          onClick={() => handleGenerateSheet(exam)}
                        >
                          <i className="bi bi-award-fill"></i>
                        </button>
                        {canManageExams && (
                          <button
                            className="btn btn-icon btn-light rounded-circle text-danger shadow-sm d-flex align-items-center justify-content-center"
                            style={{ width: 35, height: 35 }}
                            title="Delete Exam"
                            onClick={() => handleDelete(exam.id, exam.name)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="5" className="text-center py-5 text-muted">No exams scheduled.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={showModal} onClose={() => setShowModal(false)} title="Schedule New Exam">
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Exam Name</label>
            <input type="text" className="form-control" name="name" value={formData.name} onChange={handleInputChange} required placeholder="e.g. Mid Term, Final Project" />
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">{term}</label>
            <select className="form-select" name="course_id" value={formData.course_id} onChange={handleCourseChange} required>
              <option value="">Select {term}</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">{batchTerm}</label>
            <select className="form-select" name="batch_id" value={formData.batch_id} onChange={handleInputChange} required disabled={!formData.course_id}>
              <option value="">Select {batchTerm}</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.name}</option>
              ))}
            </select>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label  small text-muted fw-bold">Exam Date</label>
              <input type="date" className="form-control" name="exam_date" value={formData.exam_date} onChange={handleInputChange} required />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label  small text-muted fw-bold">Total Marks</label>
              <input type="number" className="form-control" name="total_marks" value={formData.total_marks} onChange={handleInputChange} required />
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill">Schedule</button>
          </div>
        </form>
      </Modal>

      {/* Results Management Modal */}
      <Modal
        show={showResultsModal}
        onClose={() => setShowResultsModal(false)}
        title={`Mark Results: ${selectedExam?.name}`}
        size="modal-lg"
      >
        <div className="alert alert-info py-2 small border-0 bg-info-subtle text-info mb-4">
          <i className="bi bi-info-circle me-2"></i>
          Entering marks for students in <strong>{selectedExam?.batch?.name}</strong>. Total marks for this exam: {selectedExam?.total_marks}.
        </div>

        <div className="table-responsive" style={{ maxHeight: '400px' }}>
          <table className="table table-bordered table-hover align-middle">
            <thead className="sticky-top bg-white">
              <tr>
                <th>{studentTerm} Name</th>
                <th>Roll No</th>
                <th style={{ width: '150px' }}>Obtained Marks</th>
              </tr>
            </thead>
            <tbody>
              {examStudents.length > 0 ? (
                examStudents.map(student => (
                  <tr key={student.id}>
                    <td>
                      <div className="fw-bold">{student.first_name} {student.last_name}</div>
                      <small className="text-muted">ID: {student.id}</small>
                    </td>
                    <td>{student.roll_no || '-'}</td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        max={selectedExam?.total_marks}
                        value={marksData[student.id]}
                        onChange={(e) => setMarksData({ ...marksData, [student.id]: e.target.value })}
                        placeholder="0"
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="3" className="text-center py-4 text-muted">No {terminology.students.toLowerCase()} found in this {batchTerm.toLowerCase()}.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowResultsModal(false)}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary px-4 rounded-pill"
            onClick={handleSaveResults}
            disabled={savingResults || examStudents.length === 0}
          >
            {savingResults ? 'Saving...' : 'Save All Results'}
          </button>
        </div>
      </Modal>

      {/* Result Sheet Modal */}
      <Modal
        show={showSheetModal}
        onClose={() => setShowSheetModal(false)}
        title={`Exam Result Sheet: ${sheetData.exam?.name}`}
        size="modal-xl"
      >
        <div className="p-4 bg-white rounded-3 shadow-sm border">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h4 className="fw-bold mb-1 text-primary">{sheetData.exam?.name}</h4>
              <p className="text-secondary mb-0">
                <span className="badge bg-light text-dark border me-2">{sheetData.exam?.course?.name}</span>
                <span className="badge bg-light text-dark border me-2">{sheetData.exam?.batch?.name}</span>
                <span className="text-muted small"><i className="bi bi-calendar-event me-1"></i>Exam Date: {new Date(sheetData.exam?.exam_date).toLocaleDateString()}</span>
              </p>
            </div>
            <div className="text-end">
              <div className="bg-primary-subtle p-3 rounded-3 border border-primary-subtle shadow-sm">
                <p className="text-primary small fw-bold mb-0">Total Marks</p>
                <h3 className="fw-bold text-primary mb-0">{sheetData.exam?.total_marks}</h3>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover border align-middle text-center">
              <thead className="bg-light">
                <tr>
                  <th className="text-start ps-3 py-3" style={{ width: '50px' }}>Sr.</th>
                  <th className="text-start py-3">Student Name</th>
                  <th className="py-3">Roll No</th>
                  <th className="py-3">Marks Obtained</th>
                  <th className="py-3">Percentage</th>
                  <th className="pe-3 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {sheetData.results.map((res, index) => (
                  <tr key={res.id}>
                    <td className="ps-3 fw-bold text-secondary small">{index + 1}</td>
                    <td className="text-start">
                      <div className="d-flex align-items-center gap-3">
                        <div className="avatar-sm rounded-circle bg-light d-flex align-items-center justify-content-center fw-bold text-primary border" style={{ width: '35px', height: '35px', fontSize: '14px' }}>
                          {res.first_name.charAt(0)}
                        </div>
                        <div className="fw-bold">{res.first_name} {res.last_name}</div>
                      </div>
                    </td>
                    <td className="text-muted small">{res.roll_no || '-'}</td>
                    <td className="fw-bold fs-5 text-dark">{res.obtained} <small className="text-muted fs-6 fw-normal">/ {sheetData.exam?.total_marks}</small></td>
                    <td>
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <div className="progress w-100" style={{ height: '6px' }}>
                          <div
                            className={`progress-bar rounded-pill ${res.percentage < (settings.passing_percentage || 40) ? 'bg-danger' : 'bg-success'}`}
                            role="progressbar"
                            style={{ width: `${res.percentage}%` }}
                          ></div>
                        </div>
                        <span className="fw-bold small">{res.percentage}%</span>
                      </div>
                    </td>
                    <td className="pe-3">
                      <span className={`badge rounded-pill px-3 py-2 fw-bold ${res.status === 'Pass' ? 'bg-success-subtle text-success border border-success' : 'bg-danger-subtle text-danger border border-danger'}`}>
                        {res.status.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 border-top pt-4 d-flex justify-content-between align-items-center text-muted small">
            <div>Generated on: {new Date().toLocaleDateString()}</div>
            <div className="d-flex gap-4">
              <span>Total Students: <strong>{sheetData.results.length}</strong></span>
              <span>Passed: <strong className="text-success">{sheetData.results.filter(r => r.status === 'Pass').length}</strong></span>
              <span>Failed: <strong className="text-danger">{sheetData.results.filter(r => r.status === 'Fail').length}</strong></span>
            </div>
          </div>
        </div>

        <div className="modal-footer border-0 p-0 mt-4 d-flex justify-content-end gap-3">
          <button type="button" className="btn btn-light px-4 py-2 fw-bold shadow-sm rounded-pill" onClick={() => window.print()}>
            <i className="bi bi-printer me-2"></i> Print Sheet
          </button>
          <button type="button" className="btn btn-primary px-4 py-2 fw-bold shadow-sm rounded-pill" onClick={() => setShowSheetModal(false)}>
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ExamList;
